import {
  CalendarOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FieldTimeOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Button, DatePicker, InputNumber, TimePicker } from "antd";
import dayjs from "dayjs";
import { useAntdMessage } from "hooks/useAntdMessage";
import {
  useMSAvailableSlots,
  useMSPriorityAgent,
} from "queries/meetingSchedule";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "utils/helper";

const WORK_START = 8;
const WORK_END = 17;

const DATE_OPTIONS = [
  { key: "today", offset: 0 },
  { key: "tomorrow", offset: 1 },
  { key: "day2", offset: 2 },
  { key: "day3", offset: 3 },
];

const PickButton = ({ active, className, ...props }) => (
  <button
    type="button"
    className={cn(
      "rounded-full border px-4 py-1.5 text-sm font-medium transition-all",
      active
        ? "border-primary bg-primary text-white shadow-sm"
        : "border-neutral-200 bg-neutral-50 text-neutral-700 hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10 hover:text-primary hover:shadow-md",
      className
    )}
    {...props}
  />
);

const SectionCard = ({ icon, title, children }) => (
  <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
    <div className="mb-3 flex items-center gap-2">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </span>
      <p className="mb-0 text-base font-semibold text-neutral-900">{title}</p>
    </div>
    {children}
  </div>
);

const AutoScheduleStep = ({ prev, dataForm, onChangeDataForm }) => {
  const { t } = useTranslation();
  const { message } = useAntdMessage();
  const availableSlots = useMSAvailableSlots();
  const priorityAgent = useMSPriorityAgent();

  const [in15Value] = useState(() => dayjs().add(15, "minute").format("HH:mm"));

  // Restore state tersimpan (mis. saat klik "Kembali" dari step Detail).
  const [saved] = useState(() => dataForm.auto_state || {});

  const [phase, setPhase] = useState(saved.phase || "form");
  const [dateOption, setDateOption] = useState(saved.dateOption || "today");
  const [customDate, setCustomDate] = useState(
    saved.customDate ? dayjs(saved.customDate) : null
  );
  const [selectedTime, setSelectedTime] = useState(
    saved.selectedTime ?? in15Value
  );
  const [otherTimeMode, setOtherTimeMode] = useState(
    saved.otherTimeMode || false
  );
  const [durationOption, setDurationOption] = useState(
    saved.durationOption ?? 60
  );
  const [otherDurH, setOtherDurH] = useState(saved.otherDurH ?? 0);
  const [otherDurM, setOtherDurM] = useState(saved.otherDurM ?? 30);
  const [slots, setSlots] = useState(saved.slots || []);
  const [selectedSlot, setSelectedSlot] = useState(
    saved.selectedSlot || saved.slots?.[0] || null
  );
  const [requestedLabel, setRequestedLabel] = useState(
    saved.requestedLabel || ""
  );
  const [requestedDateLabel, setRequestedDateLabel] = useState(
    saved.requestedDateLabel || ""
  );

  const selectedDate = useMemo(() => {
    if (dateOption === "custom") return customDate;
    const opt = DATE_OPTIONS.find((o) => o.key === dateOption);
    return dayjs().add(opt?.offset ?? 0, "day");
  }, [dateOption, customDate]);

  const isToday = selectedDate ? selectedDate.isSame(dayjs(), "day") : false;

  const hourOptions = useMemo(() => {
    const start = isToday ? Math.max(WORK_START, dayjs().hour() + 1) : WORK_START;
    const arr = [];
    for (let h = start; h <= WORK_END; h += 1) arr.push(h);
    return arr;
  }, [isToday]);

  const durationMinutes = useMemo(() => {
    if (durationOption === "other") {
      return (Number(otherDurH) || 0) * 60 + (Number(otherDurM) || 0);
    }
    return durationOption;
  }, [durationOption, otherDurH, otherDurM]);

  const canSubmit =
    Boolean(selectedDate) && Boolean(selectedTime) && durationMinutes >= 5;

  const submitting = availableSlots.isPending || priorityAgent.isPending;

  // Default jam = opsi paling awal: "15 menit lagi" (hari ini) atau jam kerja
  // pertama (08:00) untuk hari lain.
  const earliestTime = (isTodaySel) =>
    isTodaySel ? in15Value : `${String(WORK_START).padStart(2, "0")}:00`;

  const selectDateOption = (key) => {
    setDateOption(key);
    setOtherTimeMode(false);
    setSelectedTime(earliestTime(key === "today"));
  };

  const onPickCustomDate = (value) => {
    setCustomDate(value);
    setOtherTimeMode(false);
    setSelectedTime(earliestTime(Boolean(value && value.isSame(dayjs(), "day"))));
  };

  // Snapshot state agar saat "Kembali" dari step Detail kembali ke tampilan
  // yang sama (form / daftar slot) dengan pilihan yang sama.
  const buildAutoState = (phaseVal, slotList) => ({
    phase: phaseVal,
    dateOption,
    customDate: customDate ? customDate.toISOString() : null,
    selectedTime,
    otherTimeMode,
    durationOption,
    otherDurH,
    otherDurM,
    slots: slotList ?? slots,
    selectedSlot,
    requestedLabel,
    requestedDateLabel,
  });

  const resolveBySlot = async (dateTimeStr, persistPhase) => {
    try {
      const { data } = await priorityAgent.mutateAsync({
        admin_group_id: dataForm.admin_group_id,
        date_time: dateTimeStr,
        duration_minutes: durationMinutes,
        include_metadata: 1,
      });
      const agent = data?.data?.agent;
      if (!agent) {
        message.error(t("meeting_schedule.no_agent_available"));
        return;
      }
      const start = dayjs(dateTimeStr);
      onChangeDataForm({
        schedule_admin_id: agent.schedule_admin_id,
        date: start.format("YYYY-MM-DD"),
        start_time: start.format("HH:mm"),
        end_time: start.add(durationMinutes, "minute").format("HH:mm"),
        modeForm: "auto",
        auto_agent_name: agent.agent_name,
        auto_reasoning: data?.data?.priority_metadata ?? null,
        auto_state: buildAutoState(persistPhase),
      });
    } catch (err) {
      message.error(
        err?.response?.data?.message || t("meeting_schedule.no_agent_available")
      );
    }
  };

  const handleNext = async () => {
    if (!canSubmit) return;
    const desired = `${dayjs(selectedDate).format(
      "YYYY-MM-DD"
    )} ${selectedTime}:00`;
    try {
      const { data } = await availableSlots.mutateAsync({
        admin_group_id: dataForm.admin_group_id,
        date_time: desired,
        duration_minutes: durationMinutes,
      });
      const list = Array.isArray(data?.data) ? data.data : [];
      if (list[0] && dayjs(list[0]).isSame(dayjs(desired))) {
        await resolveBySlot(desired, "form");
      } else {
        setSlots(list);
        setSelectedSlot(list[0] || null); // default: slot terdekat
        setRequestedLabel(selectedTime);
        setRequestedDateLabel(dayjs(selectedDate).format("DD MMM YYYY"));
        setPhase("slots");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          t("meeting_schedule.auto_check_failed")
      );
    }
  };

  const groupedSlots = useMemo(() => {
    const groups = {};
    slots.forEach((s) => {
      const key = dayjs(s).format("YYYY-MM-DD");
      if (!groups[key]) groups[key] = [];
      groups[key].push(s);
    });
    return Object.entries(groups);
  }, [slots]);

  if (phase === "slots") {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        {/* Notifikasi slot penuh */}
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
            <ExclamationCircleOutlined />
          </span>
          <p className="mb-0 text-sm text-amber-800">
            {t("meeting_schedule.auto_slots_full", {
            date: requestedDateLabel,
            time: requestedLabel,
          })}
          </p>
        </div>

        {groupedSlots.length === 0 ? (
          <div className="rounded-2xl border border-neutral-200 bg-white p-8 text-center text-neutral-400 shadow-sm">
            {t("meeting_schedule.auto_no_slots")}
          </div>
        ) : (
          <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
            {groupedSlots.map(([date, items]) => (
              <SectionCard
                key={date}
                icon={<CalendarOutlined />}
                title={dayjs(date).format("dddd, DD MMM YYYY")}
              >
                <div className="flex flex-wrap gap-2">
                  {items.map((s) => (
                    <button
                      key={s}
                      type="button"
                      disabled={submitting}
                      onClick={() => setSelectedSlot(s)}
                      className={cn(
                        "rounded-full border px-4 py-1.5 text-sm font-medium transition-all disabled:opacity-60",
                        s === selectedSlot
                          ? "border-emerald-500 bg-emerald-500 text-white shadow-sm"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-500 hover:bg-emerald-500 hover:text-white"
                      )}
                    >
                      {dayjs(s).format("HH:mm")}
                    </button>
                  ))}
                </div>
              </SectionCard>
            ))}
          </div>
        )}

        <div className="flex justify-between border-t border-neutral-100 pt-3">
          <Button
            icon={<LeftOutlined />}
            onClick={() => setPhase("form")}
            disabled={submitting}
          >
            {t("meeting_schedule.back")}
          </Button>
          <Button
            type="primary"
            icon={<RightOutlined />}
            iconPosition="end"
            loading={submitting}
            disabled={!selectedSlot}
            onClick={() => resolveBySlot(selectedSlot, "slots")}
          >
            {t("meeting_schedule.next")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      {/* Kapan */}
      <SectionCard
        icon={<CalendarOutlined />}
        title={t("meeting_schedule.when_question")}
      >
        <div className="flex flex-wrap gap-2">
          {DATE_OPTIONS.map((o) => (
            <PickButton
              key={o.key}
              active={dateOption === o.key}
              onClick={() => selectDateOption(o.key)}
            >
              {t(`meeting_schedule.${o.key}`)}
            </PickButton>
          ))}
          <PickButton
            active={dateOption === "custom"}
            onClick={() => setDateOption("custom")}
          >
            {t("meeting_schedule.pick_date")}
          </PickButton>
        </div>
        {dateOption === "custom" && (
          <div className="mt-3">
            <DatePicker
              className="w-full"
              value={customDate}
              onChange={onPickCustomDate}
              format="DD MMM YYYY"
              disabledDate={(d) => d && d.isBefore(dayjs(), "day")}
            />
          </div>
        )}
      </SectionCard>

      {/* Jam */}
      {selectedDate && (
        <SectionCard
          icon={<ClockCircleOutlined />}
          title={t("meeting_schedule.pick_time")}
        >
          <div className="flex flex-wrap gap-2">
            {isToday && (
              <PickButton
                active={!otherTimeMode && selectedTime === in15Value}
                onClick={() => {
                  setOtherTimeMode(false);
                  setSelectedTime(in15Value);
                }}
              >
                {t("meeting_schedule.in_15_min")}
              </PickButton>
            )}
            {hourOptions.map((h) => {
              const v = `${String(h).padStart(2, "0")}:00`;
              return (
                <PickButton
                  key={h}
                  active={!otherTimeMode && selectedTime === v}
                  onClick={() => {
                    setOtherTimeMode(false);
                    setSelectedTime(v);
                  }}
                >
                  {t("meeting_schedule.hour_label", { hour: h })}
                </PickButton>
              );
            })}
            <PickButton
              active={otherTimeMode}
              onClick={() => setOtherTimeMode(true)}
            >
              {t("meeting_schedule.other_time")}
            </PickButton>
          </div>
          {otherTimeMode && (
            <div className="mt-3">
              <TimePicker
                className="w-full"
                format="HH:mm"
                minuteStep={5}
                allowClear={false}
                value={selectedTime ? dayjs(selectedTime, "HH:mm") : null}
                onChange={(v) => setSelectedTime(v ? v.format("HH:mm") : null)}
              />
            </div>
          )}
        </SectionCard>
      )}

      {/* Durasi */}
      <SectionCard
        icon={<FieldTimeOutlined />}
        title={t("meeting_schedule.duration_question")}
      >
        <div className="flex flex-wrap gap-2">
          <PickButton
            active={durationOption === 60}
            onClick={() => setDurationOption(60)}
          >
            {t("meeting_schedule.dur_1h")}
          </PickButton>
          {[45, 40, 30].map((m) => (
            <PickButton
              key={m}
              active={durationOption === m}
              onClick={() => setDurationOption(m)}
            >
              {t("meeting_schedule.dur_minutes", { m })}
            </PickButton>
          ))}
          <PickButton
            active={durationOption === "other"}
            onClick={() => setDurationOption("other")}
          >
            {t("meeting_schedule.other_duration")}
          </PickButton>
        </div>
        {durationOption === "other" && (
          <div className="mt-3 flex items-center gap-2">
            <InputNumber
              min={0}
              max={8}
              value={otherDurH}
              onChange={(v) => setOtherDurH(v ?? 0)}
              addonAfter={t("meeting_schedule.hours")}
            />
            <InputNumber
              min={0}
              max={59}
              value={otherDurM}
              onChange={(v) => setOtherDurM(v ?? 0)}
              addonAfter={t("meeting_schedule.minutes")}
            />
          </div>
        )}
      </SectionCard>

      <div className="flex justify-between pt-1">
        <Button
          icon={<LeftOutlined />}
          onClick={() => prev()}
          disabled={submitting}
        >
          {t("meeting_schedule.previous")}
        </Button>
        <Button
          type="primary"
          icon={<RightOutlined />}
          iconPosition="end"
          loading={submitting}
          disabled={!canSubmit}
          onClick={handleNext}
        >
          {t("meeting_schedule.next")}
        </Button>
      </div>
    </div>
  );
};

export default AutoScheduleStep;
